import axios from 'axios'
import { CircleDot } from 'lucide-vue-next'
import type { MaybeRefOrGetter, Ref } from 'vue'
import { useContentScopeStatusCriterion, type ContentScopeListState } from '@/composables/contentScopeList'
import useLocalStorage from '@/composables/useLocalStorage'
import { splitContentScopeListParams } from '@/utils/contentScope'
import { storeParams } from '@/utils/storeParams'
import type { CampaignSortKey, CampaignStatus, CampaignType } from '@/types/types'
import type { ListSortOption } from '@/types/listFilters'

export const useCampaignListQuery = (type: MaybeRefOrGetter<CampaignType>, storeFilter: Ref<number[]>) => {
  const { stores } = useLocalStorage()

  const campaignListParams = (params: Record<string, unknown>) => {
    const { statuses, scopeParams } = splitContentScopeListParams(params)

    return {
      type: toValue(type),
      ...storeParams(storeFilter.value, stores.value),
      ...scopeParams,
      ...(statuses.length > 0 && { status: statuses })
    }
  }

  const fetchCampaignTotal = async (params: Record<string, unknown>) => {
    const { data } = await axios.get<{ meta: { total: number } }>('campaigns', {
      params: { page: 1, pageSize: 1, ...campaignListParams(params) }
    })
    return data.meta.total
  }

  return { campaignListParams, fetchCampaignTotal }
}

export default function useCampaignListFilters(state: ContentScopeListState, options: { withDraft: boolean }) {
  const { t } = useI18n()

  const statuses: CampaignStatus[] = options.withDraft ? ['draft', 'scheduled', 'sent'] : ['scheduled', 'sent']

  const statusCriterion = useContentScopeStatusCriterion(state, () => ({
    label: t('pages.campaign.filters.status.label'),
    icon: CircleDot,
    options: statuses.map((value) => ({ value, label: t(`pages.campaign.filters.status.${value}`) })),
    multiple: true
  }))

  const sortOptions = computed<ListSortOption<CampaignSortKey>[]>(() => [
    { value: 'updated_at', label: t('pages.campaign.filters.sort.updatedAt') },
    { value: 'created_at', label: t('pages.campaign.filters.sort.createdAt') },
    { value: 'launch_date', label: t('pages.campaign.filters.sort.launchDate') }
  ])

  return { statusCriterion, sortOptions }
}
